import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { Box, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert } from '../../../../../../shared/lib/common/Alert';
import palette from '../../../../../../shared/lib/theme/palette';
import { flexCenter } from '../../../../../../shared/lib/theme/styleConstants';
import { useVerifyEmailTokenMutation } from '../../../../generated';
import { AUTH_TOKEN, LINKS, MSG_TOKEN_VERIFIED } from '../../../constants';
import PendingPromoAlert from '../../../../components/common/PendingPromoAlert';
import { useMobileView } from '../../../hooks/useMobileView';

const VerifyEmail = () => {
  const { green, red } = palette;
  const navigate = useNavigate();
  const [tokenValid, setTokenValid] = useState<boolean>(false);
  const [isVerified, setIsVerified] = useState<boolean>(true);
  const [searchParams] = useSearchParams();
  const userToken = searchParams.get('token');
  const shouldRunRef = useRef(true);
  const mediumViewport = useMobileView();

  const [verifyEmail] = useVerifyEmailTokenMutation({
    onError({ message }) {
      setTokenValid(false);
      Alert.error(message);
      navigate(LINKS.LOGIN_LINK);
    },

    onCompleted(data) {
      if (data) {
        setTokenValid(true);
        localStorage.setItem(AUTH_TOKEN, userToken!);
        Alert.success(MSG_TOKEN_VERIFIED);
      }
    }
  });

  const verifiedEmail = async () => {
    if (userToken) {
      await verifyEmail({
        variables: {
          token: userToken
        }
      });
    }
  };

  const init = async () => {
    shouldRunRef.current = false;
    if (userToken) {
      await verifiedEmail();

      if (isVerified) {
        setTimeout(() => {
          setIsVerified(false);
        }, 2000);
      }
    } else {
      setIsVerified(false);
      setTokenValid(true);
    }
  };

  useEffect(() => {
    if (shouldRunRef.current) {
      (async () => await init())();
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (userToken && isVerified) {
      setTimeout(() => {
        setIsVerified(false);
      }, 2000);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (tokenValid && !isVerified) {
      navigate(LINKS.LOGIN_LINK);
    }
  }, [tokenValid, isVerified]);

  return (
    <Box sx={flexCenter} height="100vh">
      {tokenValid && isVerified && (
        <Box flex={mediumViewport ? 'none' : 1} sx={{ ...flexCenter, flexDirection: 'column' }}>
          <Box textAlign="center" width={mediumViewport ? '100%' : '500px'}>
            <PendingPromoAlert />
            <Box marginBottom="1rem">
              <CheckCircleIcon sx={{ width: '64px', height: '64px', fill: green }} />
            </Box>
            <Typography variant="h5" sx={{ marginBottom: '1rem', fontWeight: '500' }}>
              Email Verified!
            </Typography>
          </Box>
        </Box>
      )}

      {!tokenValid && !isVerified && (
        <Box flex={1} sx={{ ...flexCenter, flexDirection: 'column' }}>
          <Box textAlign="center" width={mediumViewport ? '100%' : '500px'}>
            <Box marginBottom="1rem">
              <ErrorIcon sx={{ width: '64px', height: '64px', fill: red }} />
            </Box>
            <Typography variant="h5" sx={{ marginBottom: '.5rem', fontWeight: '500' }}>
              Token not verified!
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default VerifyEmail;
